import { DeleteOutlined, EditOutlined, ExclamationCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import { FormRule } from "antd";
import axios from "axios";
import { useCreateOfficeMutation, useDeleteOfficeMutation, useUpdateOfficeMutation } from "api/mutations/offices";
import KEYS from "api/queries/keys";
import { getOfficeActiveForumReferences, useOfficesQuery } from "api/queries/offices";
import { OfficeForumReferenceModel, OfficeValueModel } from "api-client/controllers/offices";
import FullHeightForm from "components/FullHeightForm";
import { Button, Col, Drawer, Form, Input, Layout, Modal, Row, Space, Table, Typography } from "components/styleguide";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

import styles from "./styles.module.less";

const { Header, Content } = Layout;

interface OfficeFormValues {
  value: string;
}

type DrawerState =
  | {
      mode: "create";
      office?: never;
    }
  | {
      mode: "edit";
      office: OfficeValueModel;
    };

const normalizeForComparison = (value?: string) => value?.trim().toLocaleLowerCase() ?? "";

const getForumReferenceDetails = (reference: OfficeForumReferenceModel) => {
  const identifiers = [reference.code].filter(Boolean).join(" / ");
  const suffix = identifiers ? ` (${identifiers})` : "";

  return `${suffix} - ${moment(reference.endDate).format("L")}`;
};

const ActiveForumReferencesList = ({
  forumDetailsBasePath,
  references,
}: {
  forumDetailsBasePath: string;
  references: OfficeForumReferenceModel[];
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const visibleReferences = isExpanded ? references : references.slice(0, 5);
  const hiddenReferencesCount = references.length - visibleReferences.length;

  return (
    <div className={isExpanded ? styles.referenceListContainerExpanded : styles.referenceListContainer}>
      <ul className={styles.referenceList}>
        {visibleReferences.map((reference) => (
          <li key={`${reference.id}-${reference.code}`}>
            <Typography.Link href={`${forumDetailsBasePath}/${reference.id}`} target="_blank" rel="noreferrer">
              {reference.title}
            </Typography.Link>
            {getForumReferenceDetails(reference)}
          </li>
        ))}
      </ul>
      {!isExpanded && hiddenReferencesCount > 0 && (
        <Typography.Link onClick={() => setIsExpanded(true)}>
          {t("and {{count}} more...", { count: hiddenReferencesCount })}
        </Typography.Link>
      )}
    </div>
  );
};

const Offices = () => {
  const { t } = useTranslation();
  const { lang } = useParams();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<OfficeFormValues>();

  const [drawerState, setDrawerState] = useState<DrawerState | null>(null);

  const officesQuery = useOfficesQuery({});
  const offices = officesQuery.data?.data ?? [];

  const createOfficeMutation = useCreateOfficeMutation();
  const updateOfficeMutation = useUpdateOfficeMutation();
  const deleteOfficeMutation = useDeleteOfficeMutation();

  const isDrawerOpen = Boolean(drawerState);
  const isMutationLoading =
    createOfficeMutation.isLoading || updateOfficeMutation.isLoading || deleteOfficeMutation.isLoading;
  const forumDetailsBasePath = lang ? `/${lang}/system-admin/forums` : "/system-admin/forums";

  const invalidateOfficeQueries = useCallback(() => {
    queryClient.invalidateQueries([KEYS.GET_OFFICES]);
    queryClient.invalidateQueries([KEYS.GET_FORUMS]);
    queryClient.invalidateQueries([KEYS.GET_FORUM_BY_ID]);
  }, [queryClient]);

  const closeDrawer = useCallback(() => {
    if (!isMutationLoading) {
      setDrawerState(null);
      form.resetFields();
    }
  }, [form, isMutationLoading]);

  useEffect(() => {
    if (!drawerState) {
      form.resetFields();
      return;
    }

    form.setFieldsValue({ value: drawerState.mode === "edit" ? drawerState.office.value : "" });
  }, [drawerState, form]);

  const getErrorMessage = useCallback(
    (error: unknown) => {
      if (axios.isAxiosError(error)) {
        const responseData = error.response?.data as { type?: string } | undefined;
        const errorType = responseData?.type;

        if (errorType === "DuplicateItem") {
          return t("An Office value with this name already exists");
        }

        if (errorType === "InvalidModelState") {
          return t("Office value cannot be blank");
        }
      }

      return error instanceof Error ? error.message : t("Something went wrong");
    },
    [t],
  );

  const renderForumReferences = useCallback(
    (references: OfficeForumReferenceModel[]) => (
      <ActiveForumReferencesList forumDetailsBasePath={forumDetailsBasePath} references={references} />
    ),
    [forumDetailsBasePath],
  );

  const validateOfficeValue = useCallback(
    async (_rule: FormRule, value?: string) => {
      const normalizedValue = normalizeForComparison(value);

      if (!normalizedValue) {
        return Promise.resolve();
      }

      const currentValue = drawerState?.mode === "edit" ? drawerState.office.value : undefined;
      const normalizedCurrentValue = normalizeForComparison(currentValue);
      const hasDuplicate = offices.some((office) => {
        const normalizedOfficeValue = normalizeForComparison(office.value);

        return normalizedOfficeValue === normalizedValue && normalizedOfficeValue !== normalizedCurrentValue;
      });

      if (hasDuplicate) {
        return Promise.reject(new Error(t("An Office value with this name already exists")));
      }

      return Promise.resolve();
    },
    [drawerState, offices, t],
  );

  const handleMutationSuccess = useCallback(
    (message: string) => {
      invalidateOfficeQueries();
      setDrawerState(null);
      form.resetFields();
      toast.success(t(message));
    },
    [form, invalidateOfficeQueries, t],
  );

  const createOffice = useCallback(
    (value: string) => {
      createOfficeMutation.mutate(
        { value },
        {
          onSuccess: () => handleMutationSuccess("Office value created successfully"),
          onError: (error) => toast.error(getErrorMessage(error)),
        },
      );
    },
    [createOfficeMutation, getErrorMessage, handleMutationSuccess],
  );

  const updateOffice = useCallback(
    (currentValue: string, value: string) => {
      updateOfficeMutation.mutate(
        { currentValue, value },
        {
          onSuccess: () => handleMutationSuccess("Office value updated successfully"),
          onError: (error) => toast.error(getErrorMessage(error)),
        },
      );
    },
    [getErrorMessage, handleMutationSuccess, updateOfficeMutation],
  );

  const handleSubmit = useCallback(
    async ({ value }: OfficeFormValues) => {
      const nextValue = value.trim();

      if (!drawerState) {
        return;
      }

      if (drawerState.mode === "create") {
        createOffice(nextValue);
        return;
      }

      const currentValue = drawerState.office.value;

      if (nextValue === currentValue) {
        closeDrawer();
        return;
      }

      try {
        const { data: activeReferences } = await getOfficeActiveForumReferences({ value: currentValue });

        if (activeReferences.length === 0) {
          updateOffice(currentValue, nextValue);
          return;
        }

        Modal.confirm({
          title: t("Update Office value"),
          icon: <ExclamationCircleOutlined />,
          content: (
            <>
              <p>
                {t(
                  "Active forums are using this Office value. Saving will update those active forum records to {{officeValue}}.",
                  { officeValue: nextValue },
                )}
              </p>
              {renderForumReferences(activeReferences)}
            </>
          ),
          okText: t("Save"),
          cancelText: t("Cancel"),
          onOk: () => updateOffice(currentValue, nextValue),
        });
      } catch (error) {
        toast.error(getErrorMessage(error));
      }
    },
    [closeDrawer, createOffice, drawerState, getErrorMessage, renderForumReferences, t, updateOffice],
  );

  const showBlockedDeleteMessage = useCallback(
    (officeValue: string, references: OfficeForumReferenceModel[]) => {
      Modal.error({
        title: t("Office value cannot be deleted"),
        content: (
          <>
            <p>
              {t(
                "The Office value {{officeValue}} cannot be deleted because it is used by active forums. Update these forums first.",
                { officeValue },
              )}
            </p>
            {renderForumReferences(references)}
          </>
        ),
      });
    },
    [renderForumReferences, t],
  );

  const deleteOffice = useCallback(
    (office: OfficeValueModel) => {
      deleteOfficeMutation.mutate(
        { value: office.value },
        {
          onSuccess: () => {
            invalidateOfficeQueries();
            toast.success(t("Office value deleted successfully"));
          },
          onError: async (error) => {
            try {
              const { data: activeReferences } = await getOfficeActiveForumReferences({ value: office.value });

              if (activeReferences.length) {
                showBlockedDeleteMessage(office.value, activeReferences);
                return;
              }
            } catch {
              // The original delete error below is more useful than a failed fallback lookup.
            }

            toast.error(getErrorMessage(error));
          },
        },
      );
    },
    [deleteOfficeMutation, getErrorMessage, invalidateOfficeQueries, showBlockedDeleteMessage, t],
  );

  const handleDelete = useCallback(
    async (office: OfficeValueModel) => {
      try {
        const { data: activeReferences } = await getOfficeActiveForumReferences({ value: office.value });

        if (activeReferences.length > 0) {
          showBlockedDeleteMessage(office.value, activeReferences);
          return;
        }

        Modal.confirm({
          title: t("Delete Office value"),
          icon: <ExclamationCircleOutlined />,
          content: (
            <p>
              {t(
                "Delete Office value {{officeValue}}? It will no longer be available in the forum Office drop down. Historic forum records will keep their saved Office text.",
                { officeValue: office.value },
              )}
            </p>
          ),
          okText: t("Delete"),
          okType: "danger",
          cancelText: t("Cancel"),
          onOk: () => deleteOffice(office),
        });
      } catch (error) {
        toast.error(getErrorMessage(error));
      }
    },
    [deleteOffice, getErrorMessage, showBlockedDeleteMessage, t],
  );

  const columns = useMemo(
    () => [
      {
        title: t("Office Value"),
        dataIndex: "value",
        key: "value",
        sorter: (left: OfficeValueModel, right: OfficeValueModel) => left.value.localeCompare(right.value),
        render: (value: string) => t(value),
      },
      {
        title: t("Actions"),
        key: "actions",
        width: 180,
        render: (_value: string, record: OfficeValueModel) => (
          <Space>
            <Button icon={<EditOutlined />} onClick={() => setDrawerState({ mode: "edit", office: record })}>
              {t("Edit")}
            </Button>
            <Button danger icon={<DeleteOutlined />} onClick={() => handleDelete(record)}>
              {t("Delete")}
            </Button>
          </Space>
        ),
      },
    ],
    [handleDelete, t],
  );

  return (
    <div className={styles.officesPage}>
      <Layout>
        <Header className={styles.header}>
          <Row id="officeControls" gutter={16}>
            <Col>
              <Button icon={<PlusOutlined />} onClick={() => setDrawerState({ mode: "create" })}>
                {t("Add Office")}
              </Button>
            </Col>
          </Row>
        </Header>
        <Content>
          <div className={styles.container}>
            <Table
              id="officesTable"
              columns={columns}
              dataSource={offices}
              loading={officesQuery.isLoading || deleteOfficeMutation.isLoading}
              rowKey="value"
              bordered
              pagination={false}
            />
          </div>
        </Content>
      </Layout>

      <Drawer
        open={isMutationLoading || isDrawerOpen}
        placement="right"
        title={drawerState?.mode === "edit" ? t("Edit Office") : t("Add Office")}
        size="large"
        destroyOnClose
        onClose={isMutationLoading ? undefined : closeDrawer}
      >
        <FullHeightForm<OfficeFormValues>
          id="officeForm"
          name="officeForm"
          form={form}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
          onFinish={handleSubmit}
          actionsPrepend={<Button onClick={closeDrawer}>{t("Cancel")}</Button>}
          actionsAppend={
            <Button type="primary" disabled={isMutationLoading} loading={isMutationLoading} htmlType="submit">
              {drawerState?.mode === "edit" ? t("Save") : t("create")}
            </Button>
          }
        >
          <Form.Item
            label={t("Office Value")}
            name="value"
            rules={[
              {
                required: true,
                whitespace: true,
                message: t("Office value cannot be blank"),
              },
              {
                max: 100,
                message: t("The character limit is {{count}}", { count: 100 }),
              },
              {
                validator: validateOfficeValue,
              },
            ]}
          >
            <Input maxLength={100} placeholder={t("placeholders.add", { prop: "$t(Office Value)" })} />
          </Form.Item>
        </FullHeightForm>
      </Drawer>
    </div>
  );
};

export default Offices;
